import { cn, Spinner } from '@crm/ui';
import { ArrowLeft, ChevronRight, LogOut } from 'lucide-react';
import { Fragment, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { getInitials } from '@/lib/utils';
import { useLogout, useSession } from '@/modules/auth/hooks';
import { ACCOUNT_ITEMS, type NavItem, type NavSection, useNavSections } from './navigation';

/**
 * A pele dos cartoes de navegacao do celular: icone em cima, nome embaixo,
 * sem borda - o contraste vem do fundo proprio e de uma elevacao curta.
 */
const TILE =
  'flex h-24 flex-col items-center justify-center gap-1.5 rounded-sm border p-2 text-center text-sm font-medium shadow-[0_2px_4px_-1px_rgba(15,23,42,0.10),0_6px_16px_-6px_rgba(15,23,42,0.20)] transition-all active:translate-y-px active:shadow-[0_1px_2px_0_rgba(15,23,42,0.10)] dark:shadow-[0_3px_10px_-3px_rgba(0,0,0,0.8)]';
/**
 * Cartao neutro com borda nitida: a cor da marca fica reservada para dizer
 * onde se esta - com tudo roxo, o destaque deixava de destacar.
 */
export const TILE_NORMAL =
  'border-slate-300 bg-card text-foreground active:bg-muted dark:border-slate-600 dark:bg-white/[0.06]';
export const TILE_ATIVO = 'border-primary bg-primary/10 text-primary';

/** Cartao que abre uma secao do menu, em vez de navegar direto. */
export function TileButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={cn(TILE, TILE_NORMAL)}>
      <Icon className="h-7 w-7 shrink-0" aria-hidden />
      <span className="leading-tight">{label}</span>
    </button>
  );
}

/** Cartao de funcao: leva direto para a tela. */
export function TileLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => cn(TILE, isActive ? TILE_ATIVO : TILE_NORMAL)}
    >
      <item.icon className="h-7 w-7 shrink-0" aria-hidden />
      <span className="leading-tight">{item.label}</span>
    </NavLink>
  );
}

/**
 * Submenu em tela cheia: sem barra do topo, sem rodape, so a volta e os
 * cartoes. Quem entrou aqui esta escolhendo uma tela, nao navegando o app.
 */
export function SectionScreen({
  section,
  onBack,
  onNavigate,
}: {
  section: NavSection;
  onBack: () => void;
  onNavigate: () => void;
}) {
  // Vai direto para o body: dentro do menu, o `fixed` ficaria preso ao
  // empilhamento dele e a barra do topo apareceria por cima da modal.
  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-200 lg:hidden dark:bg-slate-900">
      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center gap-3 px-4 py-5 text-left"
      >
        <ArrowLeft className="h-6 w-6 shrink-0" />
        <span className="text-lg font-semibold tracking-wide">{section.title}</span>
      </button>

      <div className="grid grid-cols-2 gap-3 px-4 pb-8">
        {section.items.map((item, indice) => (
          <Fragment key={item.to}>
            {item.group && item.group !== section.items[indice - 1]?.group ? (
              <p className="col-span-2 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {item.group}
              </p>
            ) : null}

            <TileLink item={item} onNavigate={onNavigate} />
          </Fragment>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Menu do celular: a lista das secoes, cada uma ocupando a largura toda.
 * Fica sob a barra do topo - e por ela que se fecha o menu; o submenu, esse
 * sim, cobre a tela inteira.
 */
export function MobileNav({ onNavigate }: { onNavigate: () => void }) {
  const sections = useNavSections();
  const [aberta, setAberta] = useState<NavSection | null>(null);
  const { user } = useSession();
  const logout = useLogout();

  if (aberta) {
    return (
      <SectionScreen
        section={aberta}
        onBack={() => setAberta(null)}
        onNavigate={() => {
          setAberta(null);
          onNavigate();
        }}
      />
    );
  }

  return (
    <nav className="flex min-h-full flex-col bg-background" aria-label="Navegacao principal">
      {user ? (
        <div className="flex items-center gap-3 border-b border-border px-4 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {getInitials(user.name)}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium">{user.name}</span>
            <span className="block truncate text-sm text-muted-foreground">{user.email}</span>
          </span>
        </div>
      ) : null}

      {sections.map((section) =>
        // Secao que e destino (o painel) leva direto, sem tela de submenu.
        section.to ? (
          <NavLink
            key={section.title}
            to={section.to}
            end={section.end}
            onClick={onNavigate}
            className="flex w-full items-center gap-3 border-b border-border px-4 py-4 text-left transition-colors active:bg-muted"
          >
            <section.icon className="h-6 w-6 shrink-0 text-primary" aria-hidden />
            <span className="flex-1 text-base font-medium">{section.title}</span>
          </NavLink>
        ) : (
          <button
            key={section.title}
            type="button"
            onClick={() => setAberta(section)}
            className="flex w-full items-center gap-3 border-b border-border px-4 py-4 text-left transition-colors active:bg-muted"
          >
            <section.icon className="h-6 w-6 shrink-0 text-primary" aria-hidden />
            <span className="flex-1 text-base font-medium">{section.title}</span>
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        ),
      )}

      <div className="mt-auto border-t border-border py-2">
        {ACCOUNT_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex h-12 items-center gap-3 px-4 text-base transition-colors',
                isActive ? 'bg-primary/10 font-medium text-primary' : 'text-foreground',
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}

        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="flex h-12 w-full items-center gap-3 px-4 text-base text-destructive transition-colors active:bg-destructive/10"
        >
          {logout.isPending ? <Spinner className="h-5 w-5" /> : <LogOut className="h-5 w-5" />}
          <span>Sair</span>
        </button>
      </div>
    </nav>
  );
}
